/**
 * chartTheme — shared Recharts styling tokens (spec 121 item 14).
 *
 * One source of truth for axis ticks, gridlines, legend, and tooltip styling
 * so VisibilityChart, SourceTrendChart, and TypeDistributionChart read as one
 * cohesive house-styled set. Visual props only — no data/series logic lives
 * here.
 *
 * Palette: Kridt semantic tokens (ink, line, action, glaze) rather than raw
 * hex, plus engine labels (re-exported from types so charts reference a
 * single source).
 *
 * Spec 234 retires engine colour entirely — the engine trend chart identifies
 * its series by direct HTML label, not colour, so there is no engine palette
 * to re-export any more. See VisibilityChart's small multiples.
 */

import { ENGINE_LABELS } from '../types/index.js';

export { ENGINE_LABELS };

/** Axis tick text + axis line stroke. */
export const AXIS_STROKE = 'var(--hub-color-ink-subtle)';

/** Soft cartesian gridlines. */
export const GRID_STROKE = 'var(--hub-color-line)';

/** Standard dashed grid pattern. */
export const GRID_DASH = '3 3';

/** Axis tick label style: 12px, Kridt's caption floor (spec 250). */
export const AXIS_TICK = { fontSize: 12, fill: AXIS_STROKE } as const;

/** Default chart margin used across the house line/composed charts. */
export const CHART_MARGIN = { top: 5, right: 10, left: 0, bottom: 5 } as const;

/**
 * Monochrome chart vocabulary (spec 234 §5). Every data mark in a chart
 * resolves to one of exactly three things: the ink, the band, or the single
 * action-coloured mark that carries the point. Categorical hue (glazes,
 * status colours) is for canvas nodes and status chips — not charts.
 */

/** The mark colour: value dashes, lines, labels-as-marks. */
export const CHART_INK = 'var(--hub-color-ink)';

/** The range/track colour: bar columns, chart backgrounds, unaccented fill. */
export const CHART_BAND = 'var(--hub-color-band)';

/** The one accent a chart is allowed: the single mark that carries the point. */
export const CHART_ACTION = 'var(--hub-color-action)';

/** Spec 256: a mark that is context, not the point — the other brands beside
 *  the selected one, which takes `CHART_ACTION`. */
export const CHART_INK_MUTED = 'var(--hub-color-ink-muted)';

/** Dash pattern for a reference line (distinct from `GRID_DASH`, which is
 *  for the cartesian grid itself — a reference line is a data mark). */
export const REFERENCE_DASH = '5 4';

/**
 * Rank ramp for multiple series that must share one chart without a
 * categorical palette. Every step is the ink hue at a different opacity, so
 * `getComputedStyle().stroke`/`.fill` resolves to the exact same colour for
 * every series (234.A.1 reads one hue) while rank stays legible: the
 * foremost series (index 0) is full-strength ink, and each following one
 * fades a step further toward the band. Identity still comes from the
 * direct HTML label beside each mark, never from a step in this ramp alone.
 */
export const INK_OPACITY_STEPS = [1, 0.72, 0.56, 0.44, 0.36, 0.3, 0.26, 0.22, 0.19, 0.16] as const;

export function inkAtStep(index: number): string {
  const opacity = INK_OPACITY_STEPS[index % INK_OPACITY_STEPS.length];
  return `color-mix(in srgb, var(--hub-color-ink) ${Math.round(opacity * 100)}%, transparent)`;
}

/** SVG `<defs>` id for the hatched-band fill pattern (a target/CI range is a
 *  hatch, never a tinted solid fill — spec 234 §5). Mount `<ChartHatchDefs />`
 *  once per chart that uses `HATCH_FILL`. */
export const CHART_HATCH_ID = 'hub-chart-hatch';

/** `fill` value referencing the mounted hatch pattern. */
export const HATCH_FILL = `url(#${CHART_HATCH_ID})`;

/** Diagonal-line hatch pattern, ink-on-transparent, for a target/CI range
 *  band. Monochrome by construction — no `fillOpacity` trick needed to keep
 *  a band from reading as a second, tinted hue. */
export function ChartHatchDefs() {
  return (
    <defs>
      <pattern
        id={CHART_HATCH_ID}
        width="6"
        height="6"
        patternTransform="rotate(45)"
        patternUnits="userSpaceOnUse"
      >
        <line x1="0" y1="0" x2="0" y2="6" stroke={CHART_INK} strokeOpacity={0.25} strokeWidth={2} />
      </pattern>
    </defs>
  );
}

/** Tooltip container chrome — a lined paper card; Kridt has no shadows. */
export const TOOLTIP_CONTAINER =
  'rounded-lg border border-slate-200 bg-white p-3';

/** Tooltip heading (the x-axis label). */
export const TOOLTIP_LABEL = 'mb-2 text-sm font-medium text-slate-700';

/** A single tooltip row. */
export const TOOLTIP_ROW = 'flex items-center gap-2 text-xs';

/** Muted secondary text inside a tooltip row (CI / sample counts). */
export const TOOLTIP_MUTED = 'text-slate-400';

/** Legend item label text. */
export const LEGEND_LABEL = 'text-xs font-medium text-slate-600';

/** Legend swatch dot dimensions (rounded, inline-block). */
export const LEGEND_DOT = 'inline-block h-2.5 w-2.5 rounded-full';
